import { useCallback } from "react";
import { useLayerListItem } from "../../../hooks";
import { LAYER_STATES } from "../../../redux/reducers/mapViewReducer";
import { ArrowDownIcon } from "../../Icons";

import Loader from "../../Dashboard/Editor/Loader/Loader";
import LayerIcon from "../../Icons/LayerIcon";
import { Tooltip } from "../../UI";
import {
  StyledLayerItemButton,
  StyledLayerLoaderContainer,
  StyledMenuItem,
  StyledMenuItemContainer,
  StyledMenuItemSection,
  StyledSidebarMenuContainer,
} from "../LayerList-styled";
import SubLayerList from "../SubLayerList/SubLayerList";
import LayerItemTitle from "./LayerItemTitle";
import ListDropdownMenu from "./ListDropdownMenu";

const LAYER_LIST_STATES = Object.freeze({
  ...LAYER_STATES,
  PARTIALLY_LOADED: "partiallyLoaded",
});

const LayerItem = ({
  view,
  title,
  information,
  t,
  config,
  groupedLayers,
  layerListVisible,
  isSidebar,
  isExport,
  getExportList,
  getLayerTitle,
  getLayerInformation,
}) => {
  const {
    showSubLayers,
    setShowSubLayers,
    subLayersViews,
    showLoader,
    renderErrorIcon,
    statusLayerIcon,
    isVisible,
    errorLayersCount,
    layerConfig,
    handleVisibilityChange,
  } = useLayerListItem({ layerListVisible, groupedLayers });

  const layerTitle = getLayerTitle ? getLayerTitle(t) : title;
  const layerInformation = getLayerInformation
    ? getLayerInformation(t)
    : information;

  const renderLoader = useCallback(() => {
    if (showLoader) {
      return (
        <StyledLayerLoaderContainer>
          <Loader
            position="absolute"
            loaderStyles={{ position: "relative", top: "10px" }}
            size="24px"
            bg="transparent"
          />
        </StyledLayerLoaderContainer>
      );
    }
    return null;
  }, [showLoader]);

  const renderStatusLayerIcon = useCallback(
    (groupedLayers) => {
      if (statusLayerIcon) {
        return (
          <StyledLayerItemButton
            onClick={() => {
              handleVisibilityChange(groupedLayers);
            }}
          >
            {statusLayerIcon}
          </StyledLayerItemButton>
        );
      }

      return null;
    },
    [handleVisibilityChange, statusLayerIcon]
  );
  const fillColor = isVisible
    ? "var(--selectionFontColor)"
    : "var(--fontColor)";
  const circleColor = isVisible
    ? "var(--selectionBg)"
    : "var(--elementBackground)";

  return (
    <StyledMenuItemContainer
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <StyledSidebarMenuContainer>
        <Tooltip
          wrapperStyles={{
            position: "absolute",
            width: "calc(100% - 22px)",
            height: "100%",
            cursor: "default",
            left: 0,
            top: 0,
          }}
          content={layerInformation}
          placement={isSidebar ? "right" : "left"}
          showOnHover
          className="text-elipsis"
          blurred={true}
          offset={isSidebar ? 40 : 20}
        />

        {layerConfig && (
          <LayerIcon
            config={config}
            t={t}
            fill={fillColor}
            opsColor={fillColor}
            circleColor={circleColor}
            width={22}
            height={22}
            wrapperStyles={{
              borderRadius: "100px",
              background: circleColor,
              width: "22px",
              height: "22px",
              minWidth: "22px",
            }}
            textStyles={{
              width: "22px",
              height: "22px",
              minWidth: "22px",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "18px",
              textAlign: "center",
              color: fillColor,
              borderRadius: "100px",
              background: circleColor,
              border: "1px solid #eee",
            }}
            layer={{
              layerConfig: layerConfig,
              title: layerTitle,
            }}
            defaultTitle={layerTitle}
          />
        )}

        <span
          style={{
            color: !isVisible && !isExport ? "rgb(138, 138, 138)" : undefined,
          }}
        >
          {layerTitle}
        </span>
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: "4px",
            zIndex: 1,
          }}
        >
          {isExport ? (
            <ListDropdownMenu
              config={config}
              groupedLayers={groupedLayers}
              information={layerInformation}
              t={t}
            />
          ) : (
            <>
              {renderLoader()}
              {renderErrorIcon(groupedLayers)}
              {renderStatusLayerIcon(groupedLayers)}
            </>
          )}
        </div>
      </StyledSidebarMenuContainer>
    </StyledMenuItemContainer>
  );

  return (
    <StyledMenuItemContainer>
      <StyledMenuItem
        bgColor={
          !isVisible || errorLayersCount === groupedLayers.length
            ? "#f1f1f1"
            : "transparent"
        }
        key={title}
      >
        {subLayersViews.length > 0 && (
          <StyledLayerItemButton
            onClick={() => {
              setShowSubLayers(!showSubLayers);
            }}
          >
            <ArrowDownIcon
              width="16"
              height="16"
              style={{
                transform: showSubLayers ? "rotate(180deg)" : "rotate(0deg)",
                transition: "all 0.5s",
              }}
            />
          </StyledLayerItemButton>
        )}
        <div>
          {layerConfig && (
            <LayerIcon
              config={config}
              layer={{
                layerConfig: layerConfig,
                title: title,
              }}
              t={t}
              width={28}
              height={28}
              textColor={config.opsColor}
              opsColor={config.opsColor}
              textStyles={{
                padding: "4px",
                borderRadius: "100px",
                background: config.opsColor,
                color: "#fff",
                fontSize: "14px",
              }}
              defaultTitle={layerTitle}
              selected
            />
          )}
        </div>
        <LayerItemTitle title={title} />
        <StyledMenuItemSection>
          {renderLoader()}
          {renderErrorIcon(groupedLayers)}
          {renderStatusLayerIcon(groupedLayers)}
          <ListDropdownMenu
            config={config}
            groupedLayers={groupedLayers}
            information={layerInformation}
            t={t}
          />
        </StyledMenuItemSection>
      </StyledMenuItem>
      <SubLayerList subLayersViews={subLayersViews} show={showSubLayers} />
    </StyledMenuItemContainer>
  );
};

export default LayerItem;
